import {
  CaretDownOutlined,
  CloseCircleFilled,
  DownOutlined,
  ExclamationCircleFilled,
  ExclamationCircleOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import {
  Button,
  Col,
  Collapse,
  Dropdown,
  Empty,
  Menu,
  Row,
  Spin,
  Table,
  Tooltip,
  message,
} from 'antd';
import moment from 'moment';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';
import {
  createSyntheticApi,
  fetchAllAPIKeys,
  reactivateApiKey,
  revokeApiKey,
  updateApiKey,
} from '../../../api/apiKey';
import { incrementPageCounter } from '../../../api/insights';
import {
  AddAPIKeyInstanceForm,
  DeactivateAPIKeyInstanceForm,
  EditAPIKeyInstanceForm,
  GeneratedAPIKeyInstance,
  ReactivateAPIKeyInstanceForm,
  SearchBar,
} from '../../../components';
import { apiKeysStatus } from '../../../constants/apiKeys';
import { CreateApiKeyRoute } from '../../../constants/routes';
import {
  APIKeyDetails,
  APIKeyInstancePayload,
  CreateAPIKeyResponseData,
  UpdateAPIKeyPayload,
} from '../../../constants/types';
import { SHORT_DATE_TIME_FORMAT } from '../../../utils/date';
import {
  diffToLocalTimeZone,
  formatDateToLocalTimeZone,
} from '../../../utils/dateFormatter';
import {
  formatErrorMessage,
  getExpiringKeyMessage,
} from '../../../utils/errorMessage';
import { dateSorter } from '../../../utils/sorter';
import { SelectionButton, Text, Wrapper } from '../../styles';
import {
  APIKeyGroupName,
  APIKeyWarning,
  ActiveFilters,
  ActiveFiltersContainer,
  ActiveFiltersLabel,
  DropdownButton,
  FiltersContainer,
  InlineCol,
  RemoveFilters,
  StatusWarning,
  StyledCheckboxGroup,
  StyledCollapse,
  StyledModal,
  StyledRangePicker,
  StyledTag,
  UnstyledMenuItem,
} from './index.styles';

const { Panel } = Collapse;

interface EllipsisTooltipProp {
  title: string | number;
}

interface EllipsisTooltipProp {
  title: string | number;
}

export const EllipsisTooltip: FC<EllipsisTooltipProp> = ({
  title,
  children,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleVisibleChange = (open: boolean) => {
    if (
      containerRef.current &&
      containerRef.current.clientWidth &&
      containerRef.current.clientWidth < containerRef.current.scrollWidth
    ) {
      setIsOpen(open);
    }
  };
  return (
    <Tooltip
      visible={isOpen}
      onVisibleChange={handleVisibleChange}
      title={title}
    >
      <div
        ref={containerRef}
        style={{
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        }}
      >
        {children}
      </div>
    </Tooltip>
  );
};

const ViewKeyInstances: FC = () => {
  const intl = useIntl();
  const { apiKeyGroupName } = useParams<{ apiKeyGroupName: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isSortedByExpiryDate, setIsSortedByExpiryDate] = useState(true);
  const [APIKeyGroupInstances, setAPIKeyGroupInstances] = useState<
    APIKeyDetails[]
  >([]);

  const usedPeriod = [
    {
      value: 7,
      label: intl.formatMessage(
        {
          id: 'ApiKeys.dropDown.LastUsed',
        },
        { number: 7 },
      ),
    },
    {
      value: 30,
      label: intl.formatMessage(
        {
          id: 'ApiKeys.dropDown.LastUsed',
        },
        { number: 30 },
      ),
    },
    {
      value: 60,
      label: intl.formatMessage(
        {
          id: 'ApiKeys.dropDown.LastUsed',
        },
        { number: 60 },
      ),
    },
    {
      value: 90,
      label: intl.formatMessage(
        {
          id: 'ApiKeys.dropDown.LastUsed',
        },
        { number: 90 },
      ),
    },
    {
      value: 0,
      label: intl.formatMessage({
        id: 'ApiKeys.dropDown.allTime',
      }),
    },
  ];

  const [activeStatusFilters, setActiveStatusFilters] = useState<any>([]);
  const [activeDateBetweenFilter, setActiveDateBetweenFilter] =
    useState<any>(undefined);
  const [lastUsedAtFilter, setLastUsedAtFilter] = useState<any>(undefined);
  const [APIKeyToSearch, setAPIKeyToSearch] = useState<string>('');
  const [earliestCreatedInstance, setEarliestCreatedInstance] = useState<any>(
    {},
  );
  const [isAddingNewAPIKey, setIsAddingNewAPIKey] = useState(false);
  const [isCustomAPIKey, setIsCustomAPIKey] = useState(false);
  const [generatedAPIKey, setGeneratedAPIKey] = useState<
    CreateAPIKeyResponseData | undefined
  >(undefined);
  const [highlightedRowIndex, setHighlightedRowIndex] = useState<number | null>(
    null,
  );
  const [APIKeyToEditValidity, setAPIKeyToEditValidity] =
    useState<APIKeyDetails | null>(null);
  const [APIKeyToDeactivate, setAPIKeyToDeactivate] =
    useState<APIKeyDetails | null>(null);
  const [APIKeyToReactivate, setAPIKeyToReactivate] =
    useState<APIKeyDetails | null>(null);

  useEffect(() => {
    if (isAddingNewAPIKey) {
      incrementPageCounter(CreateApiKeyRoute);
    }
  }, [isAddingNewAPIKey]);

  const fetchAllAPIKeyGroupInstances = useCallback(async () => {
    return fetchAllAPIKeys().then(apiKeys => {
      return apiKeys.filter(apiKey => apiKey.name === apiKeyGroupName);
    });
  }, [apiKeyGroupName]);

  const hasValidAPIKeys = APIKeyGroupInstances.some(
    apiKey => apiKey.status === 'Active',
  );

  const expiringKeyMessage = APIKeyGroupInstances.reduce(
    (acc: any, curr: APIKeyDetails) => {
      const messageDetails = getExpiringKeyMessage(
        curr,
        intl.formatMessage({
          id: 'ApiKeys.KeyGroup.expire',
        }),
      );
      if (!acc.message) {
        return messageDetails;
      } else if (
        acc.message &&
        acc.days > messageDetails.days &&
        !!messageDetails.message
      ) {
        return messageDetails;
      }
      return acc;
    },
    { message: '', days: 0 },
  ).message;

  const setAPIKeyStatusLabel = (apiKey: any) => {
    let statusLabel = apiKey.status;

    if (apiKey.status === 'Revoked') {
      statusLabel = 'Deactivated';
    } else if (moment(apiKey.activeFrom).isAfter(new Date())) {
      statusLabel = 'Active in future';
    }

    return statusLabel;
  };

  const parsedAPIKeyGroupInstances = APIKeyGroupInstances.map(
    (instance: APIKeyDetails, index) => ({
      ...instance,
      key: index,
      status: setAPIKeyStatusLabel(instance),
    }),
  );

  let tableData: any = parsedAPIKeyGroupInstances;

  if (activeStatusFilters.length > 0) {
    tableData = tableData.filter((instance: any) =>
      activeStatusFilters.includes(instance.status),
    );
  }

  if (APIKeyToSearch) {
    tableData = tableData.filter((instance: any) => {
      const regex = /[0-9a-zA-Z-]/g;
      const toAsterisk = APIKeyToSearch.slice(3, -3);
      const newKeyword =
        APIKeyToSearch.slice(0, 3) +
        toAsterisk.replace(regex, '*') +
        APIKeyToSearch.slice(APIKeyToSearch.length - 3);
      const fullMatch = instance.masked.includes(newKeyword);
      if (fullMatch) {
        return true;
      } else {
        return instance.masked.includes(APIKeyToSearch);
      }
    });
  }

  if (activeDateBetweenFilter) {
    const startDate = moment(activeDateBetweenFilter[0]);
    const endDate = moment(activeDateBetweenFilter[1]);
    tableData = tableData.filter((instance: any) =>
      moment(instance.activeFrom).isBetween(startDate, endDate),
    );
  }

  if (lastUsedAtFilter && lastUsedAtFilter.value > 0) {
    const daysAgo = moment().subtract(lastUsedAtFilter.value, 'days');
    tableData = tableData.filter((instance: any) =>
      moment(instance.lastUsedAt).isBetween(daysAgo, moment()),
    );
  }

  const columns = [
    {
      title: intl.formatMessage({
        id: 'ApiKeys.column.apiKey',
      }),
      dataIndex: 'masked',
      key: 'apiKeyName',
      ellipsis: true,
      render: (apiKeyName: string, instance: any) => {
        const expiryMessage: string = getExpiringKeyMessage(
          instance,
          'This API key will expire ',
        ).message;

        return (
          <Row>
            <APIKeyWarning>
              {expiryMessage && (
                <Tooltip
                  arrowPointAtCenter
                  placement="topLeft"
                  title={expiryMessage}
                >
                  <ExclamationCircleFilled />
                </Tooltip>
              )}
            </APIKeyWarning>
            <EllipsisTooltip title={apiKeyName}>{apiKeyName}</EllipsisTooltip>
          </Row>
        );
      },
    },
    {
      title: intl.formatMessage({
        id: 'ApiKeys.column.createdBy',
      }),
      dataIndex: 'createdBy',
      key: 'createdBy',
      ellipsis: true,
      render: (text: string) => {
        return <EllipsisTooltip title={text}>{text}</EllipsisTooltip>;
      },
    },
    {
      title: intl.formatMessage({
        id: 'ApiKeys.column.status',
      }),
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (status: string, instance: APIKeyDetails) => {
        if (status !== 'Deactivated') {
          return status;
        } else {
          return (
            <StatusWarning>
              {status}
              <Tooltip
                title={intl.formatMessage(
                  {
                    id: 'ApiKeys.column.status.tooltip',
                  },
                  {
                    admin: instance.revokedBy,
                    date: `${formatDateToLocalTimeZone(instance.revokedAt)}`,
                  },
                )}
              >
                <ExclamationCircleOutlined />
              </Tooltip>
            </StatusWarning>
          );
        }
      },
    },
    {
      title: intl.formatMessage({
        id: 'ApiKeys.column.activefrom',
      }),
      dataIndex: 'activeFrom',
      key: 'activeFrom',
      sorter: dateSorter('activeFrom'),
      showSorterTooltip: false,
      render: (activeFrom: string) => formatDateToLocalTimeZone(activeFrom),
      width: 200,
    },
    {
      title: intl.formatMessage({
        id: 'ApiKeys.column.activeuntil',
      }),
      dataIndex: 'expireAt',
      key: 'activeUntil',
      sortDirections: ['descend', 'ascend'],
      sorter: dateSorter('expireAt'),
      showSorterTooltip: false,
      render: (expireAt: string) => formatDateToLocalTimeZone(expireAt),
      width: 200,
    },
    {
      title: intl.formatMessage({
        id: 'ApiKeys.column.lastUsed',
      }),
      dataIndex: 'lastUsedAt',
      key: 'lastUsedAt',
      sorter: dateSorter('lastUsedAt'),
      showSorterTooltip: false,
      width: 160,
      render: (lastUsedAt: string) =>
        !lastUsedAt ? 'N/A' : diffToLocalTimeZone(lastUsedAt).text,
    },
    {
      title: intl.formatMessage({
        id: 'User.colum.action',
      }),
      dataIndex: 'status',
      key: 'actions',
      width: 95,
      align: 'center',
      fixed: 'right',
      className: 'table-action-column',
      render: (status: string, instance: APIKeyDetails) => (
        <>
          <Dropdown
            overlay={
              status === 'Active' || status === 'Active in future'
                ? activeItemActions(instance)
                : inactiveItemActions(instance)
            }
            trigger={['click']}
          >
            <MoreOutlined className="three-dot-icon" />
          </Dropdown>
        </>
      ),
    },
  ];

  useEffect(() => {
    fetchAllAPIKeyGroupInstances().then(apiKeyGroupInstances => {
      setAPIKeyGroupInstances(apiKeyGroupInstances);
      setEarliestCreatedInstance(
        apiKeyGroupInstances.sort(dateSorter('createdAt'))[0],
      );
      setIsLoading(false);
    });

    return () => {
      setAPIKeyGroupInstances([]);
      setEarliestCreatedInstance({});
      setHighlightedRowIndex(null);
    };
  }, [fetchAllAPIKeyGroupInstances]);

  const clearFilters = () => {
    setActiveStatusFilters([]);
    setAPIKeyToSearch('');
    setActiveDateBetweenFilter(undefined);
  };

  const apiKeyGroupHeader = (
    <Row justify="space-between" align="middle">
      <APIKeyGroupName>
        {(!hasValidAPIKeys || expiringKeyMessage) && (
          <Tooltip
            placement="topLeft"
            arrowPointAtCenter
            title={
              !hasValidAPIKeys
                ? intl.formatMessage({
                    id: 'ApiKeys.tooltip.hasValidKeys',
                  })
                : expiringKeyMessage
            }
          >
            <ExclamationCircleFilled />
          </Tooltip>
        )}
        {apiKeyGroupName}
      </APIKeyGroupName>
      <Col>
        <b>
          <FormattedMessage id="ApiKeys.collapse.label.totalApiKeys" />
        </b>
        {APIKeyGroupInstances.length}
      </Col>
    </Row>
  );

  const activeItemActions = (itemDetails: APIKeyDetails) => {
    return (
      <Menu>
        <Menu.Item
          key="edit"
          onClick={() => setAPIKeyToEditValidity(itemDetails)}
          data-testid="apikey-instance-edit"
        >
          <FormattedMessage id="ApiKeys.menuItem.editActivePeriod" />
        </Menu.Item>
        <Menu.Item
          key="deactivate"
          onClick={() => setAPIKeyToDeactivate(itemDetails)}
          data-testid="apikey-instance-deactivate"
        >
          <FormattedMessage id="ApiKeys.menuItem.deactivate" />
        </Menu.Item>
      </Menu>
    );
  };

  const inactiveItemActions = (itemDetails: APIKeyDetails) => {
    return (
      <Menu>
        <Menu.Item
          key="reactivate"
          onClick={() => setAPIKeyToReactivate(itemDetails)}
          data-testid="apikey-instance-reactivate"
        >
          <FormattedMessage id="User.modal.reActivate" />
        </Menu.Item>
      </Menu>
    );
  };

  const statusOptions = () => {
    return (
      <Menu>
        <UnstyledMenuItem key="status-options-container">
          <StyledCheckboxGroup
            key="status-options"
            value={activeStatusFilters}
            options={apiKeysStatus}
            onChange={(selectedStatus: any) => {
              setActiveStatusFilters(selectedStatus);
            }}
            data-testid="api-key-group_statusFilterOptions"
          />
        </UnstyledMenuItem>
        <Menu.Divider />
        <Menu.Item
          key="Reset"
          data-testid="api-key-group_resetStatusFilters"
          onClick={() => setActiveStatusFilters([])}
        >
          <span id="reset-status">
            <FormattedMessage id="Global.reset" />
          </span>
        </Menu.Item>
      </Menu>
    );
  };

  const usedPeriodOptions = () => {
    return (
      <Menu data-testid="api-key-group_lastUsedFilterOptions">
        {usedPeriod.map((period, index) => (
          <Menu.Item
            key={index}
            onClick={() => setLastUsedAtFilter(period)}
            className="last-used-option"
          >
            {period.label}
          </Menu.Item>
        ))}
      </Menu>
    );
  };

  const unshiftUpdatedAPIKey = (apiKeyId: string) => {
    setIsTableLoading(true);
    setIsSortedByExpiryDate(false);

    fetchAllAPIKeyGroupInstances().then(apiKeyGroupInstances => {
      if (apiKeyId) {
        const updatedAPIKeyGroupInstances = apiKeyGroupInstances.sort(
          (a: any, b: any) => {
            return a.id === apiKeyId ? -1 : b.id === apiKeyId ? 1 : 0;
          },
        );
        clearFilters();
        setAPIKeyGroupInstances(updatedAPIKeyGroupInstances);
        setIsTableLoading(false);
        setCurrentPage(1);
        setHighlightedRowIndex(0);

        const highlightedRow: any = document.getElementById('apikey-table');
        if (highlightedRow) {
          highlightedRow.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });
        }

        setTimeout(() => {
          setHighlightedRowIndex(null);
        }, 5000);
      }
    });
  };

  const removeFromActiveFilters = (filterToRemove: string) => {
    const updatedFilters = activeStatusFilters.filter(
      (filter: any) => filter !== filterToRemove,
    );

    setActiveStatusFilters(updatedFilters);
  };

  if (isLoading) {
    return (
      <Spin spinning>
        <Row justify="center" style={{ minHeight: '800px' }} />
      </Spin>
    );
  } else if (!isLoading && APIKeyGroupInstances.length === 0) {
    return (
      <>
        <Row justify="center" style={{ minHeight: '400px' }} align="middle">
          <Empty
            description={`No data found for "${apiKeyGroupName}"`}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Row>
      </>
    );
  } else {
    return (
      <>
        <Wrapper style={{ padding: 0, paddingTop: '28px' }}>
          <StyledCollapse defaultActiveKey={['apiKeyGroupDetails']}>
            <Panel header={apiKeyGroupHeader} key="apiKeyGroupDetails">
              <Row data-testid="api-key-group_summary" gutter={[10, 10]}>
                <InlineCol span={16}>
                  <b>
                    <FormattedMessage id="ApiKeys.collapse.label.description" />
                  </b>
                  <Text>{earliestCreatedInstance?.description}</Text>
                </InlineCol>
                <InlineCol span={8}>
                  <b>
                    <FormattedMessage id="ApiKeys.collapse.label.dateCreated" />
                  </b>
                  <span>
                    {formatDateToLocalTimeZone(
                      earliestCreatedInstance?.createdAt,
                    )}
                  </span>
                </InlineCol>
                <InlineCol span={16}>
                  <b>
                    <FormattedMessage id="ApiKeys.collapse.label.userGroups" />
                  </b>
                  <span>
                    {earliestCreatedInstance?.groups &&
                      earliestCreatedInstance?.groups.join(', ')}
                  </span>
                </InlineCol>
                <InlineCol span={8}>
                  <b>
                    <FormattedMessage id="ApiKeys.collapse.label.createdBy" />
                  </b>
                  <span>{earliestCreatedInstance?.createdBy}</span>
                </InlineCol>
              </Row>
            </Panel>
          </StyledCollapse>

          <div id="apikey-table" data-testid="api-key-group_instances">
            <FiltersContainer gutter={[5, 5]} align="bottom">
              <Col span={6}>
                <div className="filter-label">
                  <FormattedMessage id="ApiKeys.filter.apiKey" />
                </div>
                <SearchBar
                  onSearch={(key: string) => setAPIKeyToSearch(key)}
                  placeholder={intl.formatMessage({
                    id: 'ApiKeys.filter.searchBar',
                  })}
                  testId="api-key-instance_search"
                />
              </Col>
              <Col span={4}>
                <div className="filter-label">
                  <FormattedMessage id="ApiKeys.filter.status" />
                </div>
                <div>
                  <Dropdown overlay={statusOptions} trigger={['click']}>
                    <DropdownButton
                      className="mw-100"
                      data-testid="api-key-group_statusFilter"
                      block
                      onClick={e => e.preventDefault()}
                    >
                      <FormattedMessage id="ApiKeys.filter.bystatus" />{' '}
                      {activeStatusFilters.length > 0 &&
                        `(${activeStatusFilters.length})`}
                      <CaretDownOutlined />
                    </DropdownButton>
                  </Dropdown>
                </div>
              </Col>
              <Col span={6}>
                <div className="filter-label">
                  <FormattedMessage id="ApiKeys.filter.activeDates" />
                </div>
                <StyledRangePicker
                  className="wp-100"
                  format={SHORT_DATE_TIME_FORMAT}
                  showTime
                  allowClear
                  ranges={{
                    'This week': [
                      moment().startOf('week'),
                      moment().endOf('week'),
                    ],
                    'This Month': [
                      moment().startOf('month'),
                      moment().endOf('month'),
                    ],
                  }}
                  value={activeDateBetweenFilter}
                  onChange={(date: any) => setActiveDateBetweenFilter(date)}
                />
              </Col>
              <Col span={4}>
                <div className="filter-label">
                  <FormattedMessage id="ApiKeys.filter.lastUsed" />
                </div>
                <div>
                  <Dropdown overlay={usedPeriodOptions} trigger={['click']}>
                    <DropdownButton
                      className="wx-auto"
                      data-testid="api-key-group_lastUsedFilter"
                      block
                      onClick={e => e.preventDefault()}
                    >
                      {lastUsedAtFilter
                        ? lastUsedAtFilter.label
                        : intl.formatMessage({
                            id: 'ApiKeys.filter.dropdown.selectPeriod',
                          })}{' '}
                      <CaretDownOutlined />
                    </DropdownButton>
                  </Dropdown>
                </div>
              </Col>
              <Col span={4} style={{ textAlign: 'right' }}>
                <Dropdown
                  trigger={['click']}
                  overlay={
                    <Menu>
                      <Menu.Item key="Generate_API_key">
                        <Link
                          to="/"
                          onClick={e => {
                            e.preventDefault();
                            setIsCustomAPIKey(false);
                            setIsAddingNewAPIKey(true);
                          }}
                          className={`${
                            isAddingNewAPIKey ? 'active' : ''
                          } mw-100`}
                          data-testid="api-key-instance_add"
                        >
                          <FormattedMessage id="ApiKeys.menu.generateApikey" />
                        </Link>
                      </Menu.Item>
                      <Menu.Item key="Create_custom_API_key">
                        <Link
                          to="/"
                          onClick={e => {
                            e.preventDefault();
                            setIsCustomAPIKey(true);
                            setIsAddingNewAPIKey(true);
                          }}
                          data-testid="api-key-custom_add"
                        >
                          <FormattedMessage id="ApiKeys.menu.customApikey" />
                        </Link>
                      </Menu.Item>
                    </Menu>
                  }
                >
                  <Button
                    shape="round"
                    size="large"
                    type="default"
                    data-testid="new-api-key-btn"
                    className="wx-auto"
                  >
                    <div className="d-flex align-center lineHeight-125">
                      <FormattedMessage id="ApiKeys.btn.newApikey" />
                      <DownOutlined className="font-i-17 ml-6 mri-0" />
                    </div>
                  </Button>
                </Dropdown>
              </Col>

              {activeStatusFilters.length > 0 && (
                <Col span={24}>
                  <ActiveFiltersContainer>
                    <ActiveFiltersLabel>
                      <FormattedMessage id="ApiKeys.filter.currentFilters" />
                    </ActiveFiltersLabel>
                    <ActiveFilters className="filter-tag">
                      {activeStatusFilters.map(
                        (filter: string, index: number) => (
                          <StyledTag key={index} className="active-filter-item">
                            {filter}
                            <CloseCircleFilled
                              data-testid={`remove-filter-item-${index}`}
                              onClick={() => removeFromActiveFilters(filter)}
                            />
                          </StyledTag>
                        ),
                      )}
                    </ActiveFilters>
                    <RemoveFilters>
                      <SelectionButton
                        data-testid="clear-filters"
                        type="link"
                        onClick={() => clearFilters()}
                      >
                        <>
                          <FormattedMessage id="ApiKeys.filter.clearAll" />
                          <CloseCircleFilled />
                        </>
                      </SelectionButton>
                    </RemoveFilters>
                  </ActiveFiltersContainer>
                </Col>
              )}
            </FiltersContainer>

            <Table
              dataSource={
                isSortedByExpiryDate
                  ? tableData.sort(dateSorter('expireAt'))
                  : tableData
              }
              columns={columns as any}
              rowClassName={(_, index) =>
                index === highlightedRowIndex ? 'highlight-row' : ''
              }
              tableLayout="fixed"
              className="apikeys-table"
              loading={isTableLoading}
              pagination={{
                showSizeChanger: true,
                showQuickJumper: true,
                size: 'default',
                current: currentPage,
                onChange: pageNumber => {
                  setCurrentPage(pageNumber);
                  setHighlightedRowIndex(null);
                },
              }}
              scroll={{ x: 1033 }}
            />
          </div>

          <StyledModal
            visible={isAddingNewAPIKey}
            footer={null}
            closable={false}
            destroyOnClose
            width={600}
            data-testid="customAPIKey-modal"
          >
            {!generatedAPIKey ? (
              <AddAPIKeyInstanceForm
                onCreate={async (values: any) => {
                  const payload: APIKeyInstancePayload = {
                    userId: earliestCreatedInstance.userId,
                    activeFrom: values.activeFrom.utc().format(),
                    expiresAt: values.expiresAt.utc().format(),
                  };

                  if (values.apiKey) {
                    payload.apiKey = values.apiKey;
                  }

                  try {
                    const { status, data } = await createSyntheticApi(payload);
                    if (status === 'success') {
                      const dataResult: CreateAPIKeyResponseData = { ...data };
                      if (payload.apiKey) {
                        dataResult.apikey = payload.apiKey;
                      }
                      setGeneratedAPIKey(dataResult);
                    }
                  } catch (error: any) {
                    if (
                      error?.data?.errors.includes(
                        'USER_API_KEY_ALREADY_EXISTS',
                      )
                    ) {
                      throw error;
                    }
                    message.error(
                      formatErrorMessage(error?.data?.errors[0] || ''),
                      2,
                    );
                  }
                }}
                onCancel={() => {
                  setIsAddingNewAPIKey(false);
                  setIsCustomAPIKey(false);
                }}
                isCustomAPIKey={isCustomAPIKey}
              />
            ) : (
              <GeneratedAPIKeyInstance
                apiKey={generatedAPIKey.apikey}
                onClose={() => {
                  setIsAddingNewAPIKey(false);
                  unshiftUpdatedAPIKey(generatedAPIKey.id);
                  setGeneratedAPIKey(undefined);
                }}
              />
            )}
          </StyledModal>

          <StyledModal
            visible={!!APIKeyToEditValidity}
            footer={null}
            closable={false}
            destroyOnClose
          >
            <EditAPIKeyInstanceForm
              startDate={APIKeyToEditValidity?.activeFrom}
              endDate={APIKeyToEditValidity?.expireAt}
              status={APIKeyToEditValidity?.status}
              apiKey={APIKeyToEditValidity?.masked}
              onEdit={async (payload: any) => {
                if (APIKeyToEditValidity?.id && Object.keys(payload).length) {
                  try {
                    await updateApiKey(APIKeyToEditValidity?.id, payload);
                    unshiftUpdatedAPIKey(APIKeyToEditValidity?.id);
                    setAPIKeyToEditValidity(null);
                    message.success(
                      intl.formatMessage({
                        id: 'ApiKeys.modal.apiKeys.edited',
                      }),
                      2,
                    );
                  } catch (error: any) {
                    message.error(formatErrorMessage(error?.data?.title || ''));
                  }
                } else if (
                  APIKeyToEditValidity?.id &&
                  !Object.keys(payload).length
                ) {
                  setAPIKeyToEditValidity(null);
                  message.success(
                    intl.formatMessage({
                      id: 'ApiKeys.modal.apiKeys.edited',
                    }),
                    2,
                  );
                }
              }}
              onCancel={() => setAPIKeyToEditValidity(null)}
            />
          </StyledModal>

          <StyledModal
            visible={!!APIKeyToDeactivate}
            footer={null}
            closable={false}
            destroyOnClose
          >
            <DeactivateAPIKeyInstanceForm
              startDate={APIKeyToDeactivate?.activeFrom}
              endDate={APIKeyToDeactivate?.expireAt}
              apiKey={APIKeyToDeactivate?.masked}
              lastUsedAt={APIKeyToDeactivate?.lastUsedAt}
              status={APIKeyToDeactivate?.status}
              onDeactivate={async () => {
                if (APIKeyToDeactivate?.id) {
                  try {
                    await revokeApiKey(APIKeyToDeactivate?.id);
                    unshiftUpdatedAPIKey(APIKeyToDeactivate?.id);
                    setAPIKeyToDeactivate(null);
                    message.success(
                      intl.formatMessage({
                        id: 'ApiKeys.modal.apiKeys.deactivated',
                      }),
                      2,
                    );
                  } catch (error: any) {
                    message.error(
                      formatErrorMessage(error?.data?.errors[0] || ''),
                    );
                  }
                }
              }}
              onCancel={() => setAPIKeyToDeactivate(null)}
            />
          </StyledModal>

          <StyledModal
            visible={!!APIKeyToReactivate}
            footer={null}
            closable={false}
            destroyOnClose
          >
            <ReactivateAPIKeyInstanceForm
              status={APIKeyToReactivate?.status}
              apiKey={APIKeyToReactivate?.masked}
              inactiveSince={
                APIKeyToReactivate?.status === 'Expired'
                  ? APIKeyToReactivate?.expireAt
                  : APIKeyToReactivate?.revokedAt
              }
              revokedBy={
                APIKeyToReactivate?.status !== 'Expired'
                  ? APIKeyToReactivate?.revokedBy
                  : ''
              }
              onReactivate={async (values: any) => {
                const payload: UpdateAPIKeyPayload = {
                  activeFrom: values.activeFrom.utc().format(),
                  expireAt: values.expiresAt.utc().format(),
                };

                if (APIKeyToReactivate?.id) {
                  reactivateApiKey(APIKeyToReactivate?.id, payload)
                    .then(() => {
                      unshiftUpdatedAPIKey(APIKeyToReactivate?.id);
                      setAPIKeyToReactivate(null);
                      message.success(
                        intl.formatMessage({
                          id: 'ApiKeys.modal.apiKeys.reactivated',
                        }),
                        2,
                      );
                    })
                    .catch((error: any) =>
                      message.error(
                        formatErrorMessage(error?.data?.errors[0] || ''),
                      ),
                    );
                }
              }}
              onCancel={() => setAPIKeyToReactivate(null)}
            />
          </StyledModal>
        </Wrapper>
      </>
    );
  }
};

export default ViewKeyInstances;
